import React from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthTenant, useAuthUser } from 'store/auth';
import { formatDateToDateBRPolish } from 'utils/date_utils';
import { getMapCompanyBySlug } from 'services/company-service';
import { getPendingPolicies, getPolicyMetrics } from 'services/policies-service';
import { getComplianceProgramProgress } from '../../services/complice-program';
import { getVendorStats } from 'services/vendor-service';
import { statusControl } from '../../utils/labels_utils';
import { getPublicSiteUrl } from '../../utils/site_utils';
import { PageHelper } from '../../Components/PageHelper';
import { Spinner } from 'Components/common/Spinner';
import { Button, PageHeader } from 'Components/ui';

// ─── Layout ──────────────────────────────────────────────────────────────────

const DashboardContainer = styled.div`
  padding: 24px;
  background-color: var(--vo-mist);
`;

const DateSuffix = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-slate);
  margin: 4px 0 0;
`;

// ─── Section label ────────────────────────────────────────────────────────────

const SectionLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const SectionAccent = styled.div`
  width: 3px;
  height: 18px;
  background: ${({ $color }) => $color};
  border-radius: 2px;
  flex-shrink: 0;
`;

const SectionLabelText = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.14em;
  margin: 0;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

// ─── Cards ────────────────────────────────────────────────────────────────────

const WhiteCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 22px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const MappingCard = styled(WhiteCard)`
  border-left: 4px solid ${({ $color }) => $color};
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px color-mix(in srgb, var(--vo-escuro) 14%, transparent);
  }
`;

// ─── Stat grids ──────────────────────────────────────────────────────────────

const StatGrid4 = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatGrid5 = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatValue = styled.div`
  font-family: 'Rubik', sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  letter-spacing: -0.02em;
  line-height: 1;
  margin-bottom: 6px;
`;

const StatLabel = styled.div`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 10px;
`;

const KpiCard = styled(WhiteCard)`
  border-left: 4px solid ${({ $color }) => $color};
  padding: 24px;
`;

const KpiSubtitle = styled.div`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0;
`;

// ─── Tasks card ───────────────────────────────────────────────────────────────

const TasksCard = styled(WhiteCard)`
  padding: 20px 24px;
`;

const TaskCardTitle = styled.h6`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 16px 0;
`;

const TaskGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
`;

const TaskItem = styled.div`
  background: ${({ $bg }) => $bg};
  border-left: 4px solid ${({ $borderColor }) => $borderColor};
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const TaskItemTitle = styled.div`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--vo-ink);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TaskItemSub = styled.div`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
`;

const EmptyTasksWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
`;

const EmptyTasksText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
`;

// ─── 2-col program grid ───────────────────────────────────────────────────────

const ProgramGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CardTitle = styled.h6`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 4px 0;
`;

const CardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-slate);
  margin: 0 0 16px 0;
`;

// ─── Maturity bars ────────────────────────────────────────────────────────────

const DomainRow = styled.div`
  margin-bottom: 10px;
`;

const DomainLabel = styled.div`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
`;

const BarTrack = styled.div`
  height: 6px;
  background: var(--vo-cloud);
  border-radius: 4px;
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: linear-gradient(90deg, var(--vo-cobalto), var(--vo-capri));
  border-radius: 4px;
`;

// ─── Controls donut ───────────────────────────────────────────────────────────

const DonutWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
`;

const LegendList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-ink);
`;

const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const LegendCount = styled.span`
  margin-left: auto;
  font-family: 'Rubik', sans-serif;
  font-weight: 600;
  color: var(--vo-ink);
`;

const StackedBar = styled.div`
  height: 6px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  margin-top: 4px;
`;

const StackedSegment = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
`;


// ─── Empty/loader helpers ─────────────────────────────────────────────────────

const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

const EmptyMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
  padding: 12px 0;
`;

const NoProgramWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 0;
`;

const NoProgramText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
`;

const RegisterBtn = styled(Button).attrs({ variant: 'ghost' })`
  && {
    display: flex !important;
    align-items: center;
    background-color: var(--vo-cobalto);
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 10px;
    font-family: 'Maven Pro', sans-serif;
    font-weight: 600;
    font-size: 13px;

    &:hover {
      background-color: var(--vo-cobalto);
    }
  }
`;

// ─── SVG Donut helpers ────────────────────────────────────────────────────────

const CIRCUMFERENCE = 2 * Math.PI * 38; // ≈ 238.76

function buildSegments(statusCounts) {
  if (!statusCounts) return [];
  const total = Object.values(statusCounts).reduce((s, v) => s + v, 0);
  if (total === 0) return [];

  let cumulative = 0;
  return Object.entries(statusCounts).map(([key, count]) => {
    const len = (count / total) * CIRCUMFERENCE;
    const offset = -cumulative;
    cumulative += len;
    return { key, count, len, offset, color: statusControl[key]?.color || 'var(--vo-slate)' };
  });
}

function computeCompliantPct(statusCounts) {
  if (!statusCounts) return 0;
  const total = Object.values(statusCounts).reduce((s, v) => s + v, 0);
  if (total === 0) return 0;
  const compliant = (statusCounts.monitored || 0) + (statusCounts.notApplicable || 0);
  return Math.round((compliant / total) * 100);
}

function computeOverallPct(statusCounts) {
  if (!statusCounts) return 0;
  const total = Object.values(statusCounts).reduce((s, v) => s + v, 0);
  if (total === 0) return 0;
  const done =
    (statusCounts.monitored || 0) +
    (statusCounts.notApplicable || 0) +
    (statusCounts.implemented || 0) +
    (statusCounts.readyForReview || 0);
  return Math.round((done / total) * 100);
}

// ─── SectionLabel component ───────────────────────────────────────────────────

function SectionLabel({ color, label }) {
  return (
    <SectionLabelRow>
      <SectionAccent $color={color} />
      <SectionLabelText>{label}</SectionLabelText>
    </SectionLabelRow>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export const Dashboard = () => {
  const { t } = useTranslation(['dashboard', 'common', 'gap-analysis']);
  const navigate = useNavigate();
  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);

  const companyMap = useQuery(['getMapCompanyBySlug'], async () => {
    if (selectedTenant.slug === undefined) return null;
    const response = await getMapCompanyBySlug(selectedTenant.slug);
    return response?.data || null;
  });

  const pendingPolicies = useQuery(['getPendingPolicies'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const response = await getPendingPolicies(selectedTenant.slug, user.userId);
    return response?.data || null;
  });

  const complianceCheck = useQuery(
    ['getComplianceProgramProgress', selectedTenant.slug],
    async () => {
      if (!selectedTenant.slug) return null;
      const response = await getComplianceProgramProgress(selectedTenant.slug);
      return response?.data || null;
    },
    { enabled: !!selectedTenant.slug }
  );

  const policyMetrics = useQuery(
    ['getPolicyMetrics', selectedTenant?.slug],
    async () => {
      if (!selectedTenant?.slug) return null;
      const response = await getPolicyMetrics(selectedTenant.slug);
      return response?.data || null;
    },
    { enabled: !!selectedTenant?.slug }
  );

  const vendorStats = useQuery(
    ['getVendorStats', selectedTenant?.slug],
    async () => {
      if (!selectedTenant?.slug) return null;
      const response = await getVendorStats(selectedTenant.slug);
      return response?.data || null;
    },
    { enabled: !!selectedTenant?.slug }
  );

  const tourSteps = [
    {
      target: 'body',
      content: 'Bem-vindo ao Dashboard! Vamos fazer um tour rápido pelas principais seções desta página. Clique em "Próximo" para começar!',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="page-header"]',
      content: 'Aqui é exibido o nome da sua empresa e a data atual.',
      placement: 'bottom',
      title: 'Informações da Empresa',
    },
    {
      target: '[data-tour="overview-section"]',
      content: 'Esta seção mostra os principais indicadores do seu mapeamento: processos, áreas, sistemas e ativos.',
      placement: 'bottom',
      title: 'Visão Geral',
    },
    {
      target: '[data-tour="tasks-card"]',
      content: 'Aqui você encontra tarefas pendentes, como políticas que precisam de aceite.',
      placement: 'top',
      title: 'Tarefas Pendentes',
    },
    {
      target: '[data-tour="mapping-section"]',
      content: 'Acesse rapidamente cada módulo de mapeamento de negócios e dados clicando nos cards.',
      placement: 'top',
      title: 'Mapeamento',
    },
    {
      target: '[data-tour="program-section"]',
      content: 'Acompanhe o progresso do seu Programa de Privacidade e o status dos controles.',
      placement: 'top',
      title: 'Programa de Privacidade',
    },
    {
      target: '[data-tour="vendors-section"]',
      content: 'Veja métricas de gestão de risco de terceiros consolidadas aqui.',
      placement: 'top',
      title: 'Gestão de Terceiros',
    },
    {
      target: 'body',
      content: 'Tour concluído! Você pode reativá-lo clicando no botão de ajuda (?) no canto inferior direito.',
      placement: 'center',
      title: 'Tour Concluído!',
    },
  ];

  // ── derived data ──────────────────────────────────────────────────────────

  const statusCounts = complianceCheck.data?.statusCounts || null;
  const hasComplianceProgram = complianceCheck.data?.hasComplianceProgram;
  const segments = buildSegments(statusCounts);
  const compliantPct = computeCompliantPct(statusCounts);
  const overallPct = computeOverallPct(statusCounts);
  const totalControls = statusCounts
    ? Object.values(statusCounts).reduce((s, v) => s + v, 0)
    : 0;

  const pendingPoliciesCount = pendingPolicies.data?.length || 0;

  // Build task items — one card per pending policy
  const taskItems = (pendingPolicies.data || []).map((item) => ({
    id: item._id,
    bg: 'var(--vo-heatmap-risky)',
    borderColor: 'var(--vo-warning)',
    title: t('dashboard:main.tasks.pendingPolicy'),
    subtitle: `${item.policyName} (${item.policyVersion})`,
    onClick: () => {
      window.open(
        getPublicSiteUrl(selectedTenant.slug, `/accept-policy-version/${item._id}`),
        '_blank'
      );
    },
  }));

  return (
    <DashboardContainer>
      <PageHelper
        steps={tourSteps}
        tourKey="dashboard-tour"
        autoStart={true}
        showButton={true}
      />

      {/* ── Page Header ───────────────────────────────────────────────── */}
      <div>
        <PageHeader
          data-tour="page-header"
          title={selectedTenant?.name}
          segments={[{ label: t('common:breadcrumbs.home') }]}
        />
        <DateSuffix>{formatDateToDateBRPolish(new Date())}</DateSuffix>
      </div>

      {/* ── Section 1: Visão Geral ────────────────────────────────────── */}
      <Section data-tour="overview-section">
        <SectionLabel color="var(--vo-celeste)" label={t('dashboard:main.sections.overview')} />

        {(companyMap.isLoading || complianceCheck.isLoading || pendingPolicies.isLoading || policyMetrics.isLoading) ? (
          <LoaderWrap><Spinner $size={40} /></LoaderWrap>
        ) : (
          <StatGrid4>
            <KpiCard $color="var(--vo-celeste)">
              <StatLabel>{t('dashboard:main.stats.processesOverview')}</StatLabel>
              <StatValue $color="var(--vo-celeste)">{companyMap.data?.processes ?? 0}</StatValue>
              <KpiSubtitle>{t('dashboard:main.stats.processesOverviewSub')}</KpiSubtitle>
            </KpiCard>
            <KpiCard $color="var(--vo-success)">
              <StatLabel>{t('dashboard:main.stats.publishedPolicies')}</StatLabel>
              <StatValue $color="var(--vo-success)">{policyMetrics.data?.publishedPolicies ?? 0}</StatValue>
              <KpiSubtitle>{t('dashboard:main.stats.publishedPoliciesSub')}</KpiSubtitle>
            </KpiCard>
            <KpiCard $color="var(--vo-cobalto)">
              <StatLabel>{t('dashboard:main.stats.maturityScore')}</StatLabel>
              <StatValue $color="var(--vo-cobalto)">{overallPct}%</StatValue>
              <KpiSubtitle>{t('dashboard:main.stats.maturityScoreSub')}</KpiSubtitle>
            </KpiCard>
            <KpiCard $color="var(--vo-warning)">
              <StatLabel>{t('dashboard:main.stats.pendingTasks')}</StatLabel>
              <StatValue $color="var(--vo-warning)">{pendingPoliciesCount}</StatValue>
              <KpiSubtitle>{t('dashboard:main.stats.pendingTasksSub')}</KpiSubtitle>
            </KpiCard>
          </StatGrid4>
        )}

        {/* Tasks card */}
        <TasksCard data-tour="tasks-card" style={{ marginTop: '14px' }}>
          <TaskCardTitle>{t('dashboard:main.tasks.title')}</TaskCardTitle>

          {pendingPolicies.isLoading ? (
            <LoaderWrap><Spinner $size={32} /></LoaderWrap>
          ) : taskItems.length === 0 ? (
            <EmptyTasksWrap>
              {/* Check icon */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--vo-success)">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
              <EmptyTasksText>{t('dashboard:main.tasks.noTasks')}</EmptyTasksText>
            </EmptyTasksWrap>
          ) : (
            <TaskGrid>
              {taskItems.map((item) => (
                <TaskItem
                  key={item.id}
                  $bg={item.bg}
                  $borderColor={item.borderColor}
                  onClick={item.onClick}
                >
                  <TaskItemTitle>
                    {/* Warning icon */}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="var(--vo-warning)">
                      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
                    </svg>
                    {item.title}
                  </TaskItemTitle>
                  <TaskItemSub>{item.subtitle}</TaskItemSub>
                </TaskItem>
              ))}
            </TaskGrid>
          )}
        </TasksCard>
      </Section>

      {/* ── Section 2: Mapeamento ─────────────────────────────────────── */}
      <Section data-tour="mapping-section">
        <SectionLabel color="var(--vo-capri)" label={t('dashboard:main.sections.mapping')} />

        {companyMap.isLoading ? (
          <LoaderWrap><Spinner $size={40} /></LoaderWrap>
        ) : (
          <StatGrid5>
            <MappingCard $color="var(--vo-capri)" onClick={() => navigate('/areas')}>
              <StatValue $color="var(--vo-capri)">{companyMap.data?.areas ?? 0}</StatValue>
              <StatLabel>{t('dashboard:main.stats.areas')}</StatLabel>
            </MappingCard>
            <MappingCard $color="var(--vo-celeste)" onClick={() => navigate('/processes')}>
              <StatValue $color="var(--vo-celeste)">{companyMap.data?.processes ?? 0}</StatValue>
              <StatLabel>{t('dashboard:main.stats.processes')}</StatLabel>
            </MappingCard>
            <MappingCard $color="var(--vo-cobalto)" onClick={() => navigate('/systems')}>
              <StatValue $color="var(--vo-cobalto)">{companyMap.data?.systems ?? 0}</StatValue>
              <StatLabel>{t('dashboard:main.stats.systems')}</StatLabel>
            </MappingCard>
            <MappingCard $color="var(--vo-celeste)" onClick={() => navigate('/assets')}>
              <StatValue $color="var(--vo-celeste)">{companyMap.data?.assets ?? 0}</StatValue>
              <StatLabel>{t('dashboard:main.stats.assets')}</StatLabel>
            </MappingCard>
            <MappingCard $color="var(--vo-capri)" onClick={() => navigate('/data')}>
              <StatValue $color="var(--vo-capri)">{companyMap.data?.datas ?? 0}</StatValue>
              <StatLabel>{t('dashboard:main.stats.dataTypes')}</StatLabel>
            </MappingCard>
          </StatGrid5>
        )}
      </Section>

      {/* ── Section 3: Programa de Privacidade ───────────────────────── */}
      <Section data-tour="program-section">
        <SectionLabel color="var(--vo-cobalto)" label={t('dashboard:main.sections.program')} />

        {complianceCheck.isLoading ? (
          <LoaderWrap><Spinner $size={40} /></LoaderWrap>
        ) : (
          <ProgramGrid>
            {/* LEFT: Score de Maturidade */}
            <WhiteCard>
              <CardTitle>{t('dashboard:main.maturity.title')}</CardTitle>
              <CardSubtitle>{t('dashboard:main.maturity.subtitle')}</CardSubtitle>

              {hasComplianceProgram === false ? (
                <EmptyMsg>{t('dashboard:main.maturity.noProgramDesc')}</EmptyMsg>
              ) : !statusCounts ? (
                <EmptyMsg>{t('dashboard:main.maturity.noData')}</EmptyMsg>
              ) : (
                <div>
                  {/* Overall bar */}
                  <DomainRow>
                    <DomainLabel>
                      <span>{t('dashboard:main.maturity.overall')}</span>
                      <span>{overallPct}%</span>
                    </DomainLabel>
                    <BarTrack>
                      <BarFill $pct={overallPct} />
                    </BarTrack>
                  </DomainRow>

                  {/* Per-status bars */}
                  {Object.entries(statusCounts).map(([key, count]) => {
                    const sc = statusControl[key];
                    if (!sc) return null;
                    const pct = totalControls > 0 ? Math.round((count / totalControls) * 100) : 0;
                    return (
                      <DomainRow key={key}>
                        <DomainLabel>
                          <span style={{ color: sc.color }}>{t(sc.labelKey)}</span>
                          <span>{pct}%</span>
                        </DomainLabel>
                        <BarTrack>
                          <BarFill $pct={pct} />
                        </BarTrack>
                      </DomainRow>
                    );
                  })}
                </div>
              )}
            </WhiteCard>

            {/* RIGHT: Status dos Controles */}
            <WhiteCard>
              <CardTitle>{t('dashboard:main.controls.title')}</CardTitle>
              <CardSubtitle>
                {t('dashboard:main.controls.subtitle')}
                {totalControls > 0 && ` · ${totalControls} ${t('dashboard:main.controls.controls')}`}
              </CardSubtitle>

              {hasComplianceProgram === false ? (
                <NoProgramWrap>
                  <NoProgramText>{t('dashboard:main.maturity.noProgramDesc')}</NoProgramText>
                  <RegisterBtn onClick={() => navigate('/compliance-program/add')}>
                    {t('dashboard:main.maturity.registerProgram')}
                  </RegisterBtn>
                </NoProgramWrap>
              ) : !statusCounts ? (
                <EmptyMsg>{t('dashboard:main.maturity.noData')}</EmptyMsg>
              ) : (
                <>
                  <DonutWrap>
                    {/* SVG Donut */}
                    <svg viewBox="0 0 100 100" width="80" height="80">
                      {/* Background track */}
                      <circle
                        cx="50"
                        cy="50"
                        r="38"
                        fill="none"
                        stroke="var(--vo-cloud)"
                        strokeWidth="16"
                      />
                      {/* Segments */}
                      {segments.map((seg) => (
                        <circle
                          key={seg.key}
                          cx="50"
                          cy="50"
                          r="38"
                          fill="none"
                          stroke={seg.color}
                          strokeWidth="16"
                          strokeDasharray={`${seg.len} ${CIRCUMFERENCE}`}
                          strokeDashoffset={seg.offset}
                          transform="rotate(-90 50 50)"
                        />
                      ))}
                      {/* Center text */}
                      <text
                        x="50"
                        y="46"
                        textAnchor="middle"
                        fontSize="14"
                        fontWeight="800"
                        fontFamily="Rubik, sans-serif"
                        fill="var(--vo-ink)"
                      >
                        {compliantPct}%
                      </text>
                      <text
                        x="50"
                        y="57"
                        textAnchor="middle"
                        fontSize="7"
                        fontFamily="Lato, sans-serif"
                        fill="var(--vo-slate)"
                      >
                        {t('dashboard:main.controls.progress')}
                      </text>
                    </svg>

                    {/* Legend */}
                    <LegendList>
                      {segments.map((seg) => {
                        const sc = statusControl[seg.key];
                        return (
                          <LegendRow key={seg.key}>
                            <LegendDot $color={seg.color} />
                            <span>{sc ? t(sc.labelKey) : seg.key}</span>
                            <LegendCount>{seg.count}</LegendCount>
                          </LegendRow>
                        );
                      })}
                    </LegendList>
                  </DonutWrap>

                  {/* Stacked bar */}
                  {totalControls > 0 && (
                    <StackedBar>
                      {segments.map((seg) => (
                        <StackedSegment
                          key={seg.key}
                          $color={seg.color}
                          $pct={(seg.count / totalControls) * 100}
                        />
                      ))}
                    </StackedBar>
                  )}
                </>
              )}
            </WhiteCard>
          </ProgramGrid>
        )}
      </Section>

      {/* ── Section 4: Gestão de Risco de Terceiros ──────────────────── */}
      <Section data-tour="vendors-section">
        <SectionLabel color="var(--vo-danger)" label={t('dashboard:main.sections.thirdParty')} />

        {vendorStats.isLoading ? (
          <LoaderWrap><Spinner $size={40} /></LoaderWrap>
        ) : (
          <StatGrid4>
            <MappingCard $color="var(--vo-celeste)">
              <StatValue $color="var(--vo-celeste)">{vendorStats.data?.total ?? 0}</StatValue>
              <StatLabel>{t('dashboard:main.stats.vendors')}</StatLabel>
            </MappingCard>
            <MappingCard $color="var(--vo-warning)">
              <StatValue $color="var(--vo-warning)">{vendorStats.data?.averageTrustScore ?? 0}%</StatValue>
              <StatLabel>{t('dashboard:main.stats.trustScore')}</StatLabel>
            </MappingCard>
            <MappingCard $color="var(--vo-danger)">
              <StatValue $color="var(--vo-danger)">{vendorStats.data?.pendingRecertification ?? 0}</StatValue>
              <StatLabel>{t('dashboard:main.stats.pendingRecert')}</StatLabel>
            </MappingCard>
            <MappingCard $color="var(--vo-success)">
              <StatValue $color="var(--vo-success)">{vendorStats.data?.dpaCompliance ?? 0}%</StatValue>
              <StatLabel>{t('dashboard:main.stats.dpaCompliance')}</StatLabel>
            </MappingCard>
          </StatGrid4>
        )}
      </Section>
    </DashboardContainer>
  );
};
